import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../api/client';
import { RecurringTransaction, Category } from '../types';
import { Icon } from '../components/Icon';
import { hapticFeedback } from '../utils/telegram';
import { formatCurrency, stripEmojis, localizeCategory } from '../utils/format';
import { PremiumFeature } from '../components/PremiumFeature';
import { useAppStore } from '../store/useAppStore';
import { AddTransactionFlow } from '../components/AddTransactionFlow/AddTransactionFlow';
import { ReminderSelector } from '../components/AddTransactionFlow/ReminderSelector';
import './RecurringTransactionsScreen.css';
import './TransactionsScreen.css'; // Импортируем стили filter-chips

type FilterType = 'all' | 'income' | 'expense';

interface RecurringTransactionsScreenProps {
  isEmbedded?: boolean;
}

export const RecurringTransactionsScreen: React.FC<RecurringTransactionsScreenProps> = ({ isEmbedded = false }) => {
  const { t } = useTranslation();
  const { settings } = useAppStore();
  const [transactions, setTransactions] = useState<RecurringTransaction[]>([]);
  const [plaidTransactions, setPlaidTransactions] = useState<RecurringTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPlaid, setIsLoadingPlaid] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [editingTransaction, setEditingTransaction] = useState<RecurringTransaction | null>(null);
  const [editingPlaidTransaction, setEditingPlaidTransaction] = useState<RecurringTransaction | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddFlow, setShowAddFlow] = useState(false);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [selectedType, setSelectedType] = useState<'income' | 'expense'>('expense');
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    // Создаем таймаут промисы для каждого запроса
    const timeoutPromise = (promise: Promise<any>, timeoutMs: number, name: string) => {
      return Promise.race([
        promise,
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error(`${name} timeout after ${timeoutMs}ms`)), timeoutMs)
        )
      ]);
    };

    // 1️⃣ Загружаем ручные транзакции и категории (быстро) ==================
    try {
      setIsLoading(true);
      console.log('🔄 Loading manual recurring transactions...');
      
      const [regularData, categoriesData] = await Promise.all([
        timeoutPromise(
          apiClient.getAllRecurringTransactions(),
          10000,
          'getAllRecurringTransactions'
        ).catch((err) => {
          console.error('❌ Failed to load regular recurring:', err);
          return [];
        }),
        // Загружаем категории параллельно
        timeoutPromise(
          apiClient.getAllCategories(),
          10000,
          'getAllCategories'
        ).catch((err) => {
          console.error('❌ Failed to load categories:', err);
          return { system_categories: [], user_categories: [] };
        }),
      ]);
      
      console.log('✅ Loaded manual recurring:', regularData?.length || 0);
      setTransactions(Array.isArray(regularData) ? regularData : []);
      
      // Обрабатываем категории
      const systemCategories = (categoriesData.system_categories || []).map((cat: any) => ({
        id: cat.db_name,
        name: cat.db_name,
        emoji: cat.emoji || null,
        is_system: true,
        type: 'both' as const,
      }));
      const userCategories = (categoriesData.user_categories || []).map((catName: string) => ({
        id: catName,
        name: catName,
        emoji: null,
        is_system: false,
        type: 'both' as const,
      }));
      const allCategories: Category[] = [
        ...systemCategories.sort((a, b) => a.name.localeCompare(b.name)),
        ...userCategories.sort((a, b) => a.name.localeCompare(b.name)),
      ];
      setCategories(allCategories);
      console.log('✅ Loaded categories:', allCategories.length);
      
    } catch (error) {
      console.error('❌ Failed to load manual recurring:', error);
      setTransactions([]);
    } finally {
      setIsLoading(false);
      console.log('✅ Manual recurring loaded, starting Plaid load...');
    }

    // 2️⃣ Загружаем банковские транзакции (медленно, независимо) ============
    try {
      setIsLoadingPlaid(true);
      console.log('🏦 Loading Plaid recurring transactions...');
      
      const plaidData = await timeoutPromise(
        apiClient.getPlaidRecurringTransactions(),
        30000, // 30 секунд для Plaid
        'getPlaidRecurringTransactions'
      ).catch((err) => {
        console.warn('⚠️ Failed to load Plaid recurring (non-critical):', err);
        return [];
      });
      
      console.log('✅ Loaded Plaid recurring:', plaidData?.length || 0);
      setPlaidTransactions(Array.isArray(plaidData) ? plaidData : []);
    } catch (error) {
      console.error('❌ Failed to load Plaid recurring:', error);
      setPlaidTransactions([]);
    } finally {
      setIsLoadingPlaid(false);
      console.log('✅ Plaid recurring loaded');
    }
  };

  const handleDelete = async (transaction: RecurringTransaction) => {
    // Создаем кастомный диалог с выбором
    const choice = await new Promise<'withHistory' | 'withoutHistory' | 'cancel'>((resolve) => {
      const modal = document.createElement('div');
      modal.className = 'modal-overlay';
      
      const dialog = document.createElement('div');
      dialog.className = 'delete-recurring-modal';
      
      dialog.innerHTML = `
        <div class="delete-recurring-modal__header">
          <h3 class="delete-recurring-modal__title">Удалить шаблон?</h3>
          <p class="delete-recurring-modal__subtitle">
            Выберите, как удалить регулярный ${transaction.type === 'income' ? 'доход' : 'расход'} "${transaction.title}":
          </p>
        </div>
        <div class="delete-recurring-modal__buttons">
          <button id="deleteWithHistory" class="delete-recurring-modal__btn delete-recurring-modal__btn--danger neo-raised">
            <svg class="delete-recurring-modal__icon" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
            </svg>
            Удалить с историей
          </button>
          <button id="deleteWithoutHistory" class="delete-recurring-modal__btn delete-recurring-modal__btn--warning neo-raised">
            <svg class="delete-recurring-modal__icon" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M14.06 9.02l.92.92L5.92 19H5v-.92l9.06-9.06M17.66 3c-.25 0-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29zm-3.6 3.19L3 17.25V21h3.75L17.81 9.94l-3.75-3.75z"/>
            </svg>
            Удалить только шаблон
          </button>
          <button id="cancelDelete" class="delete-recurring-modal__btn delete-recurring-modal__btn--cancel neo-raised">
            <svg class="delete-recurring-modal__icon" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
            Отмена
          </button>
        </div>
        <p class="delete-recurring-modal__hint">
          <strong>С историей:</strong> удалить шаблон и все транзакции<br/>
          <strong>Только шаблон:</strong> оставить прошлые транзакции
        </p>
      `;
      
      modal.appendChild(dialog);
      document.body.appendChild(modal);
      
      const cleanup = () => document.body.removeChild(modal);
      
      dialog.querySelector('#deleteWithHistory')!.addEventListener('click', () => {
        cleanup();
        resolve('withHistory');
      });
      
      dialog.querySelector('#deleteWithoutHistory')!.addEventListener('click', () => {
        cleanup();
        resolve('withoutHistory');
      });
      
      dialog.querySelector('#cancelDelete')!.addEventListener('click', () => {
        cleanup();
        resolve('cancel');
      });
      
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          cleanup();
          resolve('cancel');
        }
      });
    });

    if (choice === 'cancel') {
      return;
    }

    hapticFeedback.warning();

    try {
      const withHistory = choice === 'withHistory';
      
      if (transaction.type === 'income') {
        await apiClient.deleteRecurringIncomeDefinition(transaction.id as number, withHistory);
      } else {
        await apiClient.deleteRecurringExpense(transaction.id as number, withHistory);
      }
      
      hapticFeedback.success();
      await loadTransactions();
    } catch (error) {
      console.error('Failed to delete recurring transaction:', error);
      hapticFeedback.error();
      alert(error instanceof Error ? error.message : 'Failed to delete');
    }
  };

  const handleToggle = async (transaction: RecurringTransaction) => {
    hapticFeedback.selection();

    try {
      if (transaction.type === 'income') {
        await apiClient.toggleRecurringIncomeDefinition(transaction.id as number);
      } else {
        await apiClient.toggleRecurringExpense(transaction.id as number);
      }
      
      await loadTransactions();
    } catch (error) {
      console.error('Failed to toggle recurring transaction:', error);
      hapticFeedback.error();
    }
  };

  const handleEdit = (transaction: RecurringTransaction) => {
    setEditingTransaction(transaction);
    setShowEditModal(true);
    hapticFeedback.selection();
  };

  const handleEditPlaid = (transaction: RecurringTransaction) => {
    setEditingPlaidTransaction(transaction);
    setShowEditModal(true);
    hapticFeedback.selection();
  };

  const handleDeletePlaid = async (transaction: RecurringTransaction) => {
    const confirmed = confirm(`Удалить "${transaction.title}" из регулярных?`);
    if (!confirmed) return;

    hapticFeedback.warning();

    try {
      if (transaction.plaid_transaction_id) {
        await apiClient.removePlaidRecurringTransaction(transaction.plaid_transaction_id);
      }
      hapticFeedback.success();
      await loadTransactions();
    } catch (error) {
      console.error('Failed to remove Plaid recurring transaction:', error);
      hapticFeedback.error();
      alert(error instanceof Error ? error.message : 'Failed to remove');
    }
  };

  const filteredTransactions = transactions.filter(t => {
    if (filter === 'all') return true;
    return t.type === filter;
  });

  const filteredPlaidTransactions = plaidTransactions.filter(t => {
    if (filter === 'all') return true;
    return t.type === filter;
  });

  const getNextDateDisplay = (transaction: RecurringTransaction): string => {
    // Парсим дату вручную чтобы избежать автоматической конвертации timezone
    const parseDateString = (dateStr: string): string => {
      // Формат: "2025-10-07T22:27:00" или "2025-10-07 22:27:00"
      const datePart = dateStr.split('T')[0].split(' ')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('ru', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    };

    if (transaction.next_date) {
      return parseDateString(transaction.next_date);
    }
    return parseDateString(transaction.start_date);
  };

  const getFrequencyDetails = (transaction: RecurringTransaction): string => {
    const freq = t(`recurring.frequency.${transaction.frequency}`);
    
    if (transaction.frequency === 'weekly') {
      const dow = transaction.day_of_week ?? (() => {
        // fallback: вычислим из start_date
        const d = new Date(transaction.start_date);
        // перевод JS (0=Вс) -> python-style (0=Пн)
        return (d.getDay() + 6) % 7;
      })();
      const dayName = t(`recurring.dayOfWeek.${dow}`);
      return `${freq} (${dayName})`;
    }
    
    if (transaction.frequency === 'monthly' && transaction.day_of_month) {
      return `${freq} (${transaction.day_of_month} число)`;
    }
    
    if (transaction.frequency === 'yearly') {
      const day = transaction.day_of_month ?? new Date(transaction.start_date).getDate();
      const monthNum = transaction.month ?? (new Date(transaction.start_date).getMonth() + 1);
      const monthName = new Date(2000, monthNum - 1, 1).toLocaleString('ru', { month: 'long' });
      return `${freq} (${transaction.day_of_month} ${monthName})`;
    }
    
    return freq;
  };

  if (isLoading) {
    return (
      <div className={`recurring-transactions-screen ${isEmbedded ? 'embedded' : 'page'}`}>
        <div className="recurring-transactions-loading">
          <div className="spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <PremiumFeature featureId="recurring_transactions">
      <div className={`recurring-transactions-screen ${isEmbedded ? 'embedded' : 'page'}`}>
      {/* Add Transaction Button */}
      <div style={{ padding: '10px 16px' }}>
        <button
          className="transaction-add-button"
          onClick={() => {
            hapticFeedback.medium();
            setShowTypeSelector(true);
          }}
        >
          + Добавить транзакцию
        </button>
      </div>

      {/* Filter Chips */}
      <div className="section">
        <div className="filter-chips">
          <button
            className={`filter-chip ${filter === 'all' ? 'filter-chip--active' : ''}`}
            onClick={() => { hapticFeedback.selection(); setFilter('all'); }}
          >
            <span className="filter-chip__icon">
              <Icon name="list" size={16} />
            </span>
            <span className="filter-chip__label">{t('recurring.filterAll')}</span>
          </button>
          <button
            className={`filter-chip ${filter === 'income' ? 'filter-chip--active filter-chip--income' : ''}`}
            onClick={() => { hapticFeedback.selection(); setFilter('income'); }}
          >
            <span className="filter-chip__icon">
              <Icon name="trending-up" size={16} />
            </span>
            <span className="filter-chip__label">{t('recurring.filterIncome')}</span>
          </button>
          <button
            className={`filter-chip ${filter === 'expense' ? 'filter-chip--active filter-chip--expense' : ''}`}
            onClick={() => { hapticFeedback.selection(); setFilter('expense'); }}
          >
            <span className="filter-chip__icon">
              <Icon name="trending-down" size={16} />
            </span>
            <span className="filter-chip__label">{t('recurring.filterExpense')}</span>
          </button>
        </div>
      </div>

      {/* Transactions List */}
      {filteredTransactions.length === 0 && filteredPlaidTransactions.length === 0 && !isLoadingPlaid ? (
        <div className="recurring-transactions-empty">
          <div className="recurring-transactions-empty__icon">
            <Icon name="repeat" size={64} color="var(--color-text-secondary)" />
          </div>
          <h3 className="recurring-transactions-empty__title">{t('recurring.noRecurring')}</h3>
        </div>
      ) : (
        <>
          {/* ========== СОЗДАННЫЕ ВРУЧНУЮ ========== */}
          {filteredTransactions.length > 0 && (
            <>
              <div className="section" style={{ marginTop: '16px' }}>
                <h3 className="section__title">
                  <Icon name="edit" size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                  Созданные вручную
                </h3>
              </div>
              <div className="recurring-transactions-list">
                {filteredTransactions.map((transaction) => (
                  <div
                    key={`${transaction.type}-${transaction.id}`}
                    className={`recurring-transaction-item neo-flat neo-static ${!transaction.is_active ? 'recurring-transaction-item--inactive' : ''}`}
                  >
                    <div className="recurring-transaction-item__header">
                      <div className="recurring-transaction-item__type-badge" data-type={transaction.type}>
                        <Icon 
                          name={transaction.type === 'income' ? 'income' : 'expense'} 
                          size={16} 
                          color="white" 
                        />
                        {transaction.type === 'income' ? t('recurring.flow.income') : t('recurring.flow.expense')}
                      </div>
                      
                      <div className="recurring-transaction-item__actions">
                        <button
                          className="recurring-transaction-item__action recurring-transaction-item__action--toggle neo-raised"
                          onClick={() => handleToggle(transaction)}
                          title={transaction.is_active ? t('recurring.active') : t('recurring.inactive')}
                        >
                          <Icon name={transaction.is_active ? 'pause' : 'repeat'} size={20} />
                        </button>
                        <button
                          className="recurring-transaction-item__action recurring-transaction-item__action--edit neo-raised"
                          onClick={() => handleEdit(transaction)}
                          title="Редактировать"
                        >
                          <Icon name="edit" size={20} />
                        </button>
                        <button
                          className="recurring-transaction-item__action recurring-transaction-item__action--delete neo-raised"
                          onClick={() => handleDelete(transaction)}
                        >
                          <Icon name="trash" size={20} />
                        </button>
                      </div>
                    </div>

                    <div className="recurring-transaction-item__body">
                      <div className="recurring-transaction-item__main">
                        <h4 className="recurring-transaction-item__title">{transaction.title}</h4>
                        {transaction.category && (
                          <span className="recurring-transaction-item__category">{localizeCategory(stripEmojis(transaction.category))}</span>
                        )}
                      </div>

                      <div className="recurring-transaction-item__amount" data-type={transaction.type}>
                        {formatCurrency(transaction.amount, settings?.currency)}
                      </div>
                    </div>

                    <div className="recurring-transaction-item__footer">
                      <div className="recurring-transaction-item__info">
                        <Icon name="repeat" size={14} color="#8E8E93" />
                        <span>{getFrequencyDetails(transaction)}</span>
                      </div>
                      <div className="recurring-transaction-item__info">
                        <Icon name="calendar" size={14} color="#8E8E93" />
                        <span>{getNextDateDisplay(transaction)}</span>
                      </div>
                      {transaction.notification_time && (
                        <div className="recurring-transaction-item__info">
                          <Icon name="bell" size={14} color="#8E8E93" />
                          <span>{transaction.notification_time}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* ========== ИЗ БАНКОВ (PLAID) ========== */}
          <div className="section" style={{ marginTop: filteredTransactions.length > 0 ? '24px' : '16px' }}>
            <h3 className="section__title">
              <Icon name="credit-card" size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Из банков
            </h3>
          </div>

          {isLoadingPlaid ? (
            <div className="recurring-plaid-loading neo-flat" style={{
              padding: '32px 24px',
              margin: '0 16px 16px',
              borderRadius: '16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
              textAlign: 'center'
            }}>
              <div className="spinner" style={{ width: '32px', height: '32px' }}></div>
              <div>
                <p style={{ 
                  fontSize: '15px', 
                  fontWeight: 600, 
                  color: 'var(--color-text-primary)',
                  marginBottom: '4px'
                }}>
                  Собираем данные из банков...
                </p>
                <p style={{ 
                  fontSize: '13px', 
                  color: 'var(--color-text-secondary)',
                  lineHeight: '1.4'
                }}>
                  Анализируем транзакции для определения регулярных платежей. Это может занять до 30 секунд.
                </p>
              </div>
            </div>
          ) : filteredPlaidTransactions.length > 0 ? (
            <div className="recurring-transactions-list">
                {filteredPlaidTransactions.map((transaction) => (
                  <div
                    key={`plaid-${transaction.type}-${transaction.id}`}
                    className="recurring-transaction-item neo-flat neo-static"
                  >
                    <div className="recurring-transaction-item__header">
                      <div className="recurring-transaction-item__type-badge" data-type={transaction.type}>
                        <Icon 
                          name={transaction.type === 'income' ? 'income' : 'expense'} 
                          size={16} 
                          color="white" 
                        />
                        {transaction.type === 'income' ? t('recurring.flow.income') : t('recurring.flow.expense')}
                      </div>
                      
                      <div className="recurring-transaction-item__actions">
                        <button
                          className="recurring-transaction-item__action recurring-transaction-item__action--edit neo-raised"
                          onClick={() => handleEditPlaid(transaction)}
                          title="Настроить напоминания"
                        >
                          <Icon name="bell" size={20} />
                        </button>
                        <button
                          className="recurring-transaction-item__action recurring-transaction-item__action--delete neo-raised"
                          onClick={() => handleDeletePlaid(transaction)}
                          title="Удалить из регулярных"
                        >
                          <Icon name="trash" size={20} />
                        </button>
                      </div>
                    </div>

                    <div className="recurring-transaction-item__body">
                      <div className="recurring-transaction-item__main">
                        <h4 className="recurring-transaction-item__title">{transaction.title}</h4>
                        {transaction.category && (
                          <span className="recurring-transaction-item__category">{localizeCategory(stripEmojis(transaction.category))}</span>
                        )}
                        {(transaction as any).plaid_account && (
                          <span className="recurring-transaction-item__category" style={{ marginTop: '4px', display: 'block', fontSize: '12px', opacity: 0.7 }}>
                            {(transaction as any).plaid_account} • {(transaction as any).plaid_institution}
                          </span>
                        )}
                      </div>

                      <div className="recurring-transaction-item__amount" data-type={transaction.type}>
                        {formatCurrency(transaction.amount, settings?.currency)}
                      </div>
                    </div>

                    <div className="recurring-transaction-item__footer">
                      <div className="recurring-transaction-item__info">
                        <Icon name="repeat" size={14} color="#8E8E93" />
                        <span>{getFrequencyDetails(transaction)}</span>
                      </div>
                      {transaction.notification_time && (
                        <div className="recurring-transaction-item__info">
                          <Icon name="bell" size={14} color="#8E8E93" />
                          <span>{transaction.notification_time}</span>
                        </div>
                      )}
                      {transaction.reminders_enabled && (
                        <div className="recurring-transaction-item__info">
                          <Icon name="check" size={14} color="#34C759" />
                          <span>Напоминания включены</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="recurring-plaid-empty neo-flat" style={{
              padding: '24px',
              margin: '0 16px 16px',
              borderRadius: '16px',
              textAlign: 'center'
            }}>
              <Icon name="info" size={32} color="var(--color-text-secondary)" style={{ marginBottom: '8px' }} />
              <p style={{ 
                fontSize: '14px', 
                color: 'var(--color-text-secondary)',
                lineHeight: '1.4'
              }}>
                Регулярные транзакции из банков не найдены
              </p>
            </div>
          )}
        </>
      )}

      {/* Модальное окно редактирования - для обычных транзакций */}
      {showEditModal && editingTransaction && !editingPlaidTransaction && (
        <div className="modal-overlay">
          <div className="modal-content modal-content--full">
            <AddTransactionFlow
              onClose={() => {
                setShowEditModal(false);
                setEditingTransaction(null);
              }}
              onSubmit={async (data) => {
                try {
                  if (editingTransaction.type === 'income') {
                    await apiClient.updateRecurringIncomeDefinition(editingTransaction.id as number, {
                      title: data.title,
                      default_amount: data.amount,
                      frequency: data.frequency as 'daily' | 'weekly' | 'monthly' | 'yearly',
                      start_date: data.date ? data.date.toISOString().split('T')[0] : undefined,
                      notification_time: data.notificationTime,
                      reminder_times: data.reminderTimes,
                      reminder_offsets: data.reminderOffsets,
                      reminders_enabled: data.remindersEnabled,
                      description: data.description,
                    });
                  } else {
                    await apiClient.updateRecurringExpense(editingTransaction.id as number, {
                      title: data.title,
                      amount: data.amount,
                      category: data.category,
                      expense_type: data.frequency as 'daily' | 'weekly' | 'monthly' | 'yearly',
                      start_date_recurring: data.date ? data.date.toISOString().split('T')[0] : undefined,
                      notification_time: data.notificationTime,
                      reminder_times: data.reminderTimes,
                      reminder_offsets: data.reminderOffsets,
                      reminders_enabled: data.remindersEnabled,
                      description: data.description,
                      payment_method_id: data.payment_method_id,
                    });
                  }
                  
                  hapticFeedback.success();
                  setShowEditModal(false);
                  setEditingTransaction(null);
                  await loadTransactions();
                } catch (error) {
                  console.error('Error updating recurring transaction:', error);
                  hapticFeedback.error();
                  throw error;
                }
              }}
              isPremium={true}
              currency={settings?.currency || 'RUB'}
              currencyCode={settings?.currency || 'RUB'}
              initialType={editingTransaction.type as 'income' | 'expense'}
              categories={categories}
              initialData={{
                type: editingTransaction.type as 'income' | 'expense',
                frequency: editingTransaction.frequency,
                title: editingTransaction.title,
                amount: editingTransaction.amount,
                category: editingTransaction.category || undefined,
                date: editingTransaction.start_date ? (() => {
                  try {
                    // Парсим дату вручную чтобы избежать автоматической конвертации timezone
                    const dateStr = String(editingTransaction.start_date);
                    // Берем только дату, игнорируем время
                    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr.split(' ')[0];
                    const [year, month, day] = datePart.split('-').map(Number);
                    if (!year || !month || !day) {
                      console.error('Invalid date parts:', { year, month, day });
                      return new Date();
                    }
                    return new Date(year, month - 1, day);
                  } catch (error) {
                    console.error('Error parsing date:', error, editingTransaction.start_date);
                    return new Date();
                  }
                })() : new Date(),
                dayOfWeek: editingTransaction.day_of_week !== undefined ? editingTransaction.day_of_week : undefined,
                dayOfMonth: editingTransaction.day_of_month !== undefined ? editingTransaction.day_of_month : undefined,
                notificationTime: editingTransaction.notification_time || '09:00',
                reminderTimes: editingTransaction.reminder_times || [],
                reminderOffsets: editingTransaction.reminder_offsets || [0],
                remindersEnabled: editingTransaction.reminders_enabled || false,
                description: editingTransaction.description || '',
                payment_method_id: editingTransaction.payment_method_id || undefined,
              }}
            />
          </div>
        </div>
      )}

      {/* Модальное окно редактирования напоминаний для банковских регулярных транзакций */}
      {showEditModal && editingPlaidTransaction && (
        <div className="modal-overlay">
          <div className="modal-content modal-content--full">
            <ReminderSelector
              reminderTimes={editingPlaidTransaction.reminder_times || []}
              reminderOffsets={editingPlaidTransaction.reminder_offsets || [0]}
              remindersEnabled={editingPlaidTransaction.reminders_enabled || false}
              onBack={() => {
                setShowEditModal(false);
                setEditingPlaidTransaction(null);
              }}
              onChange={async (reminderData) => {
                try {
                  if (editingPlaidTransaction.plaid_transaction_id) {
                    await apiClient.updatePlaidRecurringReminder(
                      editingPlaidTransaction.plaid_transaction_id,
                      {
                        notification_time: editingPlaidTransaction.notification_time || '09:00',
                        reminder_times: reminderData.reminder_times,
                        reminder_offsets: reminderData.reminder_offsets,
                        reminders_enabled: reminderData.reminders_enabled,
                      }
                    );
                  }
                  hapticFeedback.success();
                  setShowEditModal(false);
                  setEditingPlaidTransaction(null);
                  await loadTransactions();
                } catch (error) {
                  console.error('Error updating Plaid recurring reminder:', error);
                  hapticFeedback.error();
                  alert(error instanceof Error ? error.message : 'Failed to update reminder');
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Transaction Type Selector Modal */}
      {showTypeSelector && (
        <div
          className="modal-overlay"
          onClick={() => setShowTypeSelector(false)}
        >
          <div
            className="modal-content modal-content--transaction-type"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="modal-content__title">
              Выберите тип транзакции
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Доход */}
              <button
                className="neo-raised"
                onClick={() => {
                  hapticFeedback.selection();
                  setSelectedType('income');
                  setShowTypeSelector(false);
                  setShowAddFlow(true);
                }}
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: '14px',
                  background: 'var(--neo-bg)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  color: 'var(--color-success)',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <Icon name="trending-up" size={24} color="var(--color-success)" />
                <span>Добавить доход</span>
              </button>

              {/* Расход */}
              <button
                className="neo-raised"
                onClick={() => {
                  hapticFeedback.selection();
                  setSelectedType('expense');
                  setShowTypeSelector(false);
                  setShowAddFlow(true);
                }}
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: '14px',
                  background: 'var(--neo-bg)',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  color: 'var(--color-danger)',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <Icon name="trending-down" size={24} color="var(--color-danger)" />
                <span>Добавить расход</span>
              </button>

              {/* Отмена */}
              <button
                className="neo-raised"
                onClick={() => {
                  hapticFeedback.selection();
                  setShowTypeSelector(false);
                }}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '14px',
                  background: 'var(--neo-bg)',
                  border: 'none',
                  color: 'var(--color-text-secondary)',
                  fontSize: '16px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginTop: '8px',
                }}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Transaction Flow */}
      {showAddFlow && (
        <div className="modal-overlay">
          <div className="modal-content modal-content--full">
            <AddTransactionFlow
              onClose={() => {
                setShowAddFlow(false);
              }}
              onSubmit={async (data) => {
                console.log('📥 [RecurringScreen] Received data from AddTransactionFlow:', {
                  type: data.type,
                  reminderTimes: data.reminderTimes,
                  reminderOffsets: data.reminderOffsets,
                  remindersEnabled: data.remindersEnabled
                });

                try {
                  if (data.type === 'income') {
                    const incomePayload = {
                      title: data.title,
                      default_amount: data.amount,
                      frequency: data.frequency as 'daily' | 'weekly' | 'monthly' | 'yearly',
                      start_date: data.date ? data.date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                      notification_time: data.notificationTime || '09:00',
                      reminder_times: data.reminderTimes,
                      reminder_offsets: data.reminderOffsets,
                      reminders_enabled: data.remindersEnabled,
                      description: data.description,
                    };
                    console.log('📤 [RecurringScreen] Sending income to API:', incomePayload);
                    await apiClient.addRecurringIncomeDefinition(incomePayload);
                  } else {
                    const expensePayload = {
                      title: data.title,
                      amount: data.amount,
                      category: data.category || 'Разное',
                      expense_type: data.frequency as 'weekly' | 'monthly' | 'yearly',
                      start_date_recurring: data.date ? data.date.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                      notification_time: data.notificationTime || '09:00',
                      reminder_times: data.reminderTimes,
                      reminder_offsets: data.reminderOffsets,
                      reminders_enabled: data.remindersEnabled,
                      description: data.description,
                      payment_method_id: data.payment_method_id,
                    };
                    console.log('📤 [RecurringScreen] Sending expense to API:', expensePayload);
                    await apiClient.addRecurringExpense(expensePayload);
                  }
                  
                  hapticFeedback.success();
                  setShowAddFlow(false);
                  await loadTransactions();
                } catch (error: any) {
                  console.error('Error creating recurring transaction:', error);
                  hapticFeedback.error();
                  
                  // Обработка ошибки дублирования названия
                  if (error?.response?.status === 409 && error?.response?.data?.code === 'DUPLICATE_TITLE') {
                    throw new Error(error.response.data.error);
                  }
                  
                  throw error;
                }
              }}
              isPremium={true}
              currency={settings?.currency || 'RUB'}
              currencyCode={settings?.currency || 'RUB'}
              initialType={selectedType}
              categories={categories}
              recurringOnly={true}
            />
          </div>
        </div>
      )}
      </div>
    </PremiumFeature>
  );
};
